import { Session as SupabaseSession, User as SupabaseUser } from '@supabase/supabase-js';
import { User as FirebaseUser, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut } from 'firebase/auth';
// Initialize Clients directly here to avoid circular dependencies

import { createClient } from '@supabase/supabase-js';
import { auth as firebaseAuth } from '../lib/firebase';

const backendType = import.meta.env.VITE_BACKEND_TYPE || 'supabase';

// Initialize Supabase Client (Moved from AuthProvider)
// Only initialize if NOT using Firebase to avoid CORS errors from auto-refresh attempts
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
export const supabaseClient = (backendType !== 'firebase' && supabaseUrl && supabaseKey) ? createClient(supabaseUrl, supabaseKey) : null;

export interface User {
    id: string;
    email?: string;
}

export interface AuthSession {
    access_token: string;
    user: User;
}

export interface AuthService {
    signIn(email: string, password: string): Promise<{ user: User | null; error: string | null }>;
    signUp(email: string, password: string): Promise<{ user: User | null; error: string | null }>;
    signOut(): Promise<void>;
    getSession(): Promise<AuthSession | null>;
    onAuthStateChange(callback: (session: AuthSession | null) => void): () => void;
}

class SupabaseAuthStrategy implements AuthService {
    async signIn(email: string, password: string) {
        if (!supabaseClient) return { user: null, error: "Supabase not configured" };
        const { data, error } = await supabaseClient.auth.signInWithPassword({ email, password });
        if (error) return { user: null, error: error.message };
        return { user: data.user ? { id: data.user.id, email: data.user.email } : null, error: null };
    }

    async signUp(email: string, password: string) {
        if (!supabaseClient) return { user: null, error: "Supabase not configured" };
        const { data, error } = await supabaseClient.auth.signUp({ email, password });
        if (error) return { user: null, error: error.message };
        return { user: data.user ? { id: data.user.id, email: data.user.email } : null, error: null };
    }

    async signOut() {
        if (!supabaseClient) return;
        await supabaseClient.auth.signOut();
    }

    async getSession() {
        if (!supabaseClient) return null;
        const { data: { session } } = await supabaseClient.auth.getSession();
        if (!session) return null;
        return {
            access_token: session.access_token,
            user: { id: session.user.id, email: session.user.email }
        };
    }

    onAuthStateChange(callback: (session: AuthSession | null) => void) {
        if (!supabaseClient) return () => { };
        const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((_event, session) => {
            callback(session ? {
                access_token: session.access_token,
                user: { id: session.user.id, email: session.user.email }
            } : null);
        });
        return () => subscription.unsubscribe();
    }
}

class FirebaseAuthStrategy implements AuthService {
    async signIn(email: string, password: string) {
        if (!firebaseAuth) return { user: null, error: "Firebase not configured" };
        try {
            const result = await signInWithEmailAndPassword(firebaseAuth, email, password);
            return { user: { id: result.user.uid, email: result.user.email || '' }, error: null };
        } catch (e: any) {
            return { user: null, error: e.message };
        }
    }

    async signUp(email: string, password: string) {
        if (!firebaseAuth) return { user: null, error: "Firebase not configured" };
        try {
            const result = await createUserWithEmailAndPassword(firebaseAuth, email, password);
            return { user: { id: result.user.uid, email: result.user.email || '' }, error: null };
        } catch (e: any) {
            return { user: null, error: e.message };
        }
    }

    async signOut() {
        if (!firebaseAuth) return;
        await firebaseSignOut(firebaseAuth);
    }

    async getSession(): Promise<AuthSession | null> {
        if (!firebaseAuth) return null;

        // If user is already loaded, use it directly
        if (firebaseAuth.currentUser) {
            const token = await firebaseAuth.currentUser.getIdToken();
            return {
                access_token: token,
                user: { id: firebaseAuth.currentUser.uid, email: firebaseAuth.currentUser.email || '' }
            };
        }

        // Otherwise, wait for auth state to initialize
        return new Promise((resolve: (value: AuthSession | null) => void) => {
            const unsubscribe = firebaseAuth!.onAuthStateChanged(async (user) => {
                unsubscribe(); // Unsubscribe immediately after getting the first state

                if (user) {
                    const token = await user.getIdToken();
                    resolve({
                        access_token: token,
                        user: { id: user.uid, email: user.email || '' }
                    });
                } else {
                    resolve(null);
                }
            });
        });
    }

    onAuthStateChange(callback: (session: AuthSession | null) => void) {
        if (!firebaseAuth) return () => { };
        const unsubscribe = firebaseAuth.onAuthStateChanged(async (user) => {
            if (user) {
                const token = await user.getIdToken();
                callback({
                    access_token: token,
                    user: { id: user.uid, email: user.email || '' }
                });
            } else {
                callback(null);
            }
        });
        return unsubscribe;
    }
}

// Factory to choose strategy based on env var
export const authService = backendType === 'firebase' ? new FirebaseAuthStrategy() : new SupabaseAuthStrategy();
